import { useMemo, useCallback } from 'react';
import { useAppStore } from '../store';
import type { Category } from '../types';
import {
  buildCategoryTree,
  findCategoryById,
  getCategoryWithAncestors,
  getCategoryDescendants,
  getRootCategories,
  getChildCategories,
  generateId,
  type CategoryNode,
} from '../services/categoryService';

export type { CategoryNode };

export function useCategories() {
  const categories = useAppStore((state) => state.categories);
  return categories;
}

export function useCategoryTree() {
  const categories = useAppStore((state) => state.categories);

  return useMemo(() => buildCategoryTree(categories), [categories]);
}

export function useRootCategories() {
  const categories = useAppStore((state) => state.categories);

  return useMemo(() => getRootCategories(categories), [categories]);
}

export function useCategory(id: string) {
  const categories = useAppStore((state) => state.categories);

  return useMemo(() => findCategoryById(categories, id), [categories, id]);
}

export function useCategoryPath(id: string) {
  const categories = useAppStore((state) => state.categories);

  return useMemo(
    () => getCategoryWithAncestors(categories, id),
    [categories, id]
  );
}

export function useCategoryDescendants(id: string) {
  const categories = useAppStore((state) => state.categories);

  return useMemo(
    () => getCategoryDescendants(categories, id),
    [categories, id]
  );
}

export function useCategoryChildren(parentId: string) {
  const categories = useAppStore((state) => state.categories);

  return useMemo(
    () => getChildCategories(categories, parentId),
    [categories, parentId]
  );
}

export function useAddCategory() {
  const categories = useAppStore((state) => state.categories);
  const addCategory = useAppStore((state) => state.addCategory);

  return useCallback(
    (data: Omit<Category, 'id'>) => {
      const existingIds = new Set(categories.map((cat) => cat.id));
      const baseId = generateId(data.name);
      let id = baseId;
      let counter = 1;

      while (existingIds.has(id)) {
        id = `${baseId}-${counter}`;
        counter++;
      }

      addCategory({ ...data, id });
    },
    [categories, addCategory]
  );
}

export function useUpdateCategory() {
  const updateCategory = useAppStore((state) => state.updateCategory);

  return useCallback(
    (id: string, updates: Partial<Omit<Category, 'id'>>) => {
      updateCategory(id, updates);
    },
    [updateCategory]
  );
}

const SIN_CATEGORIA_ID = 'sin-categoria';

export function useDeleteCategory() {
  const categories = useAppStore((state) => state.categories);
  const transactions = useAppStore((state) => state.transactions);
  const deleteCategory = useAppStore((state) => state.deleteCategory);
  const updateCategory = useAppStore((state) => state.updateCategory);
  const updateTransaction = useAppStore((state) => state.updateTransaction);

  return useCallback(
    (id: string) => {
      const descendants = getCategoryDescendants(categories, id);
      const idsToDelete = new Set([id, ...descendants.map((d) => d.id)]);

      transactions
        .filter((t) => t.categoryId && idsToDelete.has(t.categoryId))
        .forEach((t) => {
          updateTransaction(t.id, { categoryId: SIN_CATEGORIA_ID });
        });

      categories
        .filter((cat) => cat.parentId && idsToDelete.has(cat.parentId))
        .forEach((cat) => {
          updateCategory(cat.id, { parentId: null });
        });

      idsToDelete.forEach((catId) => {
        deleteCategory(catId);
      });
    },
    [
      categories,
      transactions,
      deleteCategory,
      updateCategory,
      updateTransaction,
    ]
  );
}

export interface CategoryWithTransactionCount {
  id: string;
  name: string;
  parentId: string | null;
  color: string;
  icon?: string;
  transactionCount: number;
}

export function useCategoriesWithTransactionCount(): CategoryWithTransactionCount[] {
  const categories = useAppStore((state) => state.categories);
  const transactions = useAppStore((state) => state.transactions);

  return useMemo(() => {
    const directCountMap = new Map<string, number>();

    transactions.forEach((t) => {
      if (t.categoryId) {
        directCountMap.set(
          t.categoryId,
          (directCountMap.get(t.categoryId) ?? 0) + 1
        );
      }
    });

    const getTotalCount = (categoryId: string): number => {
      const directCount = directCountMap.get(categoryId) ?? 0;
      const descendants = getCategoryDescendants(categories, categoryId);
      const descendantCount = descendants.reduce(
        (sum, desc) => sum + (directCountMap.get(desc.id) ?? 0),
        0
      );
      return directCount + descendantCount;
    };

    return categories.map((cat) => ({
      ...cat,
      transactionCount: getTotalCount(cat.id),
    }));
  }, [categories, transactions]);
}
